import React, {useState, useEffect, useCallback} from "react";
import {useTheme} from "@mui/material/styles";
import {
    Container,
    Typography,
    Button,
    Box,
    Grid,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress
} from "@mui/material";
import {Snackbar, Alert} from '@mui/material';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TodayIcon from "@mui/icons-material/Today";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ClassSchedule from "../components/ClassSchedule";
import {getClassesForSubdomain} from "../api/classesApi";
import {getClassLeaderboard} from "../api/leaderboardApi";
import { Helmet } from 'react-helmet-async';
import GuestRegistrationModal from "../components/GuestRegistrationModal";
import { formatClassTimeOnly } from "../utils/classModalHelpers";
import { API_URL } from "../config/apiConfig";

export default function MarketingPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Affiliate state from original MarketingPage
    const [affiliate, setAffiliate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Classes state from Classes.js
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [isClassModalOpen, setClassModalOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());

    const [selectedDate, setSelectedDate] = useState(new Date());

    const todayIndex = new Date().getDay();
    const correctedIndex = todayIndex === 0 ? 6 : todayIndex - 1;
    const [selectedDayIndex, setSelectedDayIndex] = useState(correctedIndex);

    const [attendeesCount, setAttendeesCount] = useState({});
    const [showWeekly, setShowWeekly] = useState(false);
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    // Day leaderboard state
    const [isDayLeaderboardOpen, setDayLeaderboardOpen] = useState(false);
    const [dayLeaderboardFilter, setDayLeaderboardFilter] = useState("all");
    const [dayLeaderboardData, setDayLeaderboardData] = useState([]);
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationSeverity, setNotificationSeverity] = useState('info');

    const [isLoading, setIsLoading] = useState(true);

    // Guest registration modal state
    const [isGuestModalOpen, setGuestModalOpen] = useState(false);
    const [selectedFreeClass, setSelectedFreeClass] = useState(null);

    // Fetch affiliate by subdomain (from original MarketingPage)
    useEffect(() => {
        const fetchAffiliateBySubdomain = async () => {
            try {
                const hostname = window.location.hostname;
                
                // Skip subdomain fetching for IP addresses (local network testing)
                if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname)) {
                    console.log('IP address detected, skipping subdomain fetch');
                    setLoading(false);
                    return;
                }
                
                let subdomain;

                if (hostname.includes('localhost')) {
                    subdomain = hostname.split('.')[0];
                } else {
                    subdomain = hostname.split('.')[0];
                }

                const response = await fetch (`${API_URL}/affiliate-by-subdomain?subdomain=${subdomain}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setAffiliate(data);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching affiliate:', err);
                setError('Failed to load affiliate information');
                setLoading(false);
            }
        };

        fetchAffiliateBySubdomain();
    }, []);

    // Modified fetchClasses from Classes.js to use affiliate from subdomain
    const fetchClasses = useCallback(async () => {
        if (!affiliate?.id) {
            return;
        }

        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() === 0 ? 6 : startOfWeek.getDay() - 1));
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        try {
            const response = await getClassesForSubdomain(affiliate.id, startOfWeek);
            setClasses(response || []);
        } catch (error) {
            console.error("❌ Fetch error:", error);
            setClasses([]);
        }
    }, [affiliate?.id, currentDate]);

    // Fetch classes when affiliate is loaded
    useEffect(() => {
        let active = true;

        if (affiliate?.id) {
            const loadClasses = async () => {
                setIsLoading(true);
                await fetchClasses();
                if (active) {
                    setIsLoading(false);
                }
            };

            loadClasses();
        }

        return () => {
            active = false;
        };
    }, [affiliate?.id, fetchClasses]);

    // Check URL for registration parameter and auto-open modal
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const registerClassId = urlParams.get('register');
        const classDateParam = urlParams.get('classDate');
        
        if (registerClassId && classDateParam && affiliate?.id) {
            // Parse the class date and set the current date to that week
            const targetClassDate = new Date(classDateParam);
            
            // Check if we need to change the week
            const targetWeekStart = new Date(targetClassDate);
            targetWeekStart.setDate(targetWeekStart.getDate() - (targetWeekStart.getDay() === 0 ? 6 : targetWeekStart.getDay() - 1));
            targetWeekStart.setHours(0, 0, 0, 0);
            
            const currentWeekStart = new Date(currentDate);
            currentWeekStart.setDate(currentWeekStart.getDate() - (currentWeekStart.getDay() === 0 ? 6 : currentWeekStart.getDay() - 1));
            currentWeekStart.setHours(0, 0, 0, 0);
            
            // If the target class is in a different week, update currentDate
            if (targetWeekStart.getTime() !== currentWeekStart.getTime()) {
                setCurrentDate(targetClassDate);
                return; // Let the fetchClasses effect handle loading the classes
            }
            
            // If we're already in the right week and classes are loaded
            if (classes.length > 0) {
                const targetClass = classes.find(cls => cls.id === parseInt(registerClassId));
                
                if (targetClass && targetClass.freeClass) {
                    // Set the selected day index to match the class date
                    const classDayOfWeek = targetClassDate.getDay();
                    const correctedDayIndex = classDayOfWeek === 0 ? 6 : classDayOfWeek - 1;
                    setSelectedDayIndex(correctedDayIndex);
                    
                    // Open the guest registration modal for this class
                    setSelectedFreeClass(targetClass);
                    setGuestModalOpen(true);
                    
                    // Optional: Clean up URL after opening modal
                    // window.history.replaceState({}, document.title, window.location.pathname);
                }
            }
        }
    }, [classes, currentDate, affiliate?.id]); // Trigger when classes, currentDate, or affiliate changes

    // Get classes for selected day (from Classes.js)
    const getClassesForSelectedDay = (dayIndex) => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() || 7) + 1 + dayIndex);

        return classes.filter((cls) => {
            const classDate = new Date(cls.time);
            return (
                classDate.getFullYear() === startOfWeek.getFullYear() &&
                classDate.getMonth() === startOfWeek.getMonth() &&
                classDate.getDate() === startOfWeek.getDate()
            );
        });
    };

    const showNotification = (message, severity = 'info') => {
        setNotificationMessage(message);
        setNotificationSeverity(severity);
        setNotificationOpen(true);
    };

    // Fetch leaderboard data for all WOD classes on the selected day (from Classes.js)
    const fetchDayLeaderboard = async (dayIndex) => {
        try {
            setLoadingLeaderboard(true);

            const dayClasses = getClassesForSelectedDay(dayIndex);
            const wodClasses = dayClasses.filter(cls => cls.trainingType === "WOD");

            if (wodClasses.length === 0) {
                showNotification("No WOD classes found for this day", "warning");
                setLoadingLeaderboard(false);
                return;
            }

            const leaderboardPromises = wodClasses.map(cls => getClassLeaderboard(cls.id));
            const leaderboardResults = await Promise.all(leaderboardPromises);

            const combinedLeaderboard = [];
            leaderboardResults.forEach((results, index) => {
                if (Array.isArray(results) && results.length > 0) {
                    const classData = wodClasses[index];
                    const classLeaderboard = results.map(entry => ({
                        ...entry,
                        className: classData.trainingName,
                        wodName: classData.wodName || "WOD",
                        classTime: formatClassTimeOnly(classData.time),
                        classId: classData.id
                    }));
                    combinedLeaderboard.push(...classLeaderboard);
                }
            });

            setDayLeaderboardData(combinedLeaderboard);
            setLoadingLeaderboard(false);

            if (combinedLeaderboard.length > 0) {
                setDayLeaderboardOpen(true);
            } else {
                showNotification("No leaderboard entries found for WOD classes today");
            }

        } catch (error) {
            console.error("Error fetching day leaderboard:", error);
            showNotification("Error loading leaderboard data", "error");
            setLoadingLeaderboard(false);
        }
    };

    // Open day leaderboard (from Classes.js)
    const handleOpenDayLeaderboard = () => {
        fetchDayLeaderboard(selectedDayIndex);
    };

    // Handle leaderboard filter change (from Classes.js)
    const handleDayLeaderboardFilterChange = (event, newFilter) => {
        if (newFilter) {
            setDayLeaderboardFilter(newFilter);
        }
    };

    // Filter the day leaderboard data (from Classes.js)
    const filteredDayLeaderboard = dayLeaderboardData.filter(entry =>
        dayLeaderboardFilter === "all" || entry.scoreType?.toLowerCase() === dayLeaderboardFilter
    );

    // Navigation handlers (from Classes.js)
    const handlePrevWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() - 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(0);
    };

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);

        const todayIndex = today.getDay();
        const correctedIndex = todayIndex === 0 ? 6 : todayIndex - 1;
        setSelectedDayIndex(correctedIndex);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(0);
    };

    const handleDaySelect = (index) => {
        setSelectedDayIndex(index);
    };

    const handleToggleView = () => {
        setShowWeekly((prev) => !prev);
    };

    // Class click handler - open guest registration for free classes
    const handleClassClick = (cls) => {
        if (cls.freeClass) {
            setSelectedFreeClass(cls);
            setGuestModalOpen(true);
        } else {
            showNotification(`Class: ${cls.trainingName} at ${formatClassTimeOnly(cls.time)}`, "info");
        }
    };

    const handleGuestRegistrationSuccess = () => {
        showNotification("Successfully registered for the class! We look forward to seeing you.", "success");
        // Refresh classes to update enrollment count
        fetchClasses();
    };

    // Check if there are any WOD classes for the selected day (from Classes.js)
    const hasWodClasses = getClassesForSelectedDay(selectedDayIndex).some(cls => cls.trainingType === "WOD");

    // Format date for display (from Classes.js)
    const getFormattedDate = () => {
        const date = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() - (currentDate.getDay() || 7) + selectedDayIndex + 1
        );

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    };

    // Loading states (from original MarketingPage)
    if (loading) return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography>Loading...</Typography>
            </Box>
        </Container>
    );

    if (error) return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        </Container>
    );

    if (!affiliate) return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <Typography>Affiliate not found</Typography>
            </Box>
        </Container>
    )

    return (
        <Box>
            <Helmet>
                <title>{affiliate.name} | IronTrack</title>
                <meta name="description" content={`${affiliate.name} - ${affiliate.trainingType} in ${affiliate.address}`} />
            </Helmet>

            <Container maxWidth={false} sx={{pl: {xs: 0, md: 2, lg: 2}, pr: {xs: 0, md: 2, lg: 2}}}>
                <Box textAlign="center" my={1}>
                    {isLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 999
                            }}
                        >
                            <CircularProgress size={60} />
                        </Box>
                    )}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        my={3}
                        px={2}
                    >
                        {/* Left side: Affiliate name and title */}
                        <Box>
                            {affiliate?.name && (
                                <Typography variant="h4" color="primary" sx={{ mb: 0 }}>
                                    {affiliate.name}
                                </Typography>
                            )}
                        </Box>

                        {/* Right side: Day Leaderboard Trophy Icon */}
                        <Box display="flex" alignItems="center" gap={1}>
                            {hasWodClasses && !showWeekly && (
                                <IconButton
                                    color="primary"
                                    onClick={handleOpenDayLeaderboard}
                                    sx={{
                                        color: "goldenrod",
                                        "&:hover": {
                                            backgroundColor: "rgba(218,165,32,0.1)"
                                        }
                                    }}
                                >
                                    <EmojiEventsIcon/>
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Week/Day Navigation (from Classes.js) */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.default',
                        zIndex: 1000,
                        py: 0,
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                    }}
                >
                    <IconButton
                        color="primary"
                        onClick={handlePrevWeek}
                        sx={{bgcolor: 'rgba(0, 0, 0, 0.04)'}}
                    >
                        <ArrowBackIosNewIcon/>
                    </IconButton>

                    {!showWeekly && (
                        <Box display="flex" alignItems="center">
                            <Typography variant="h6" sx={{mr: 1}}>
                                {getFormattedDate()}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TodayIcon/>}
                                onClick={handleToday}
                            >
                                Today
                            </Button>
                        </Box>
                    )}

                    {!isMobile && (
                        <Button variant="contained" color="primary" onClick={handleToggleView}>
                            {showWeekly ? "As Day" : "Show Weekly"}
                        </Button>
                    )}

                    <IconButton
                        color="primary"
                        onClick={handleNextWeek}
                        sx={{bgcolor: 'rgba(0, 0, 0, 0.04)'}}
                    >
                        <ArrowForwardIosIcon/>
                    </IconButton>
                </Box>

                {/* Day Selection (from Classes.js) */}
                {!showWeekly && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        p={1}
                        sx={{
                            position: 'sticky',
                            top: 35,
                            backgroundColor: 'background.default',
                            zIndex: 999,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            flexWrap: "nowrap",
                        }}
                    >
                        {dayNames.map((day, index) => (
                            <Button
                                key={index}
                                variant={selectedDayIndex === index ? "contained" : "outlined"}
                                color={selectedDayIndex === index ? "primary" : "default"}
                                onClick={() => handleDaySelect(index)}
                                sx={{
                                    minWidth: {xs: "40px", sm: "80px"},
                                    flexGrow: 1,
                                    fontSize: {xs: "0.75rem", sm: "1rem"},
                                    padding: {xs: "4px", sm: "8px"},
                                }}
                            >
                                {day}
                            </Button>
                        ))}
                    </Box>
                )}

                {/* Classes Display (from Classes.js) */}
                {showWeekly ? (
                    <Grid container spacing={2}>
                        {dayNames.map((day, index) => {
                            const dailyClasses = getClassesForSelectedDay(index);
                            const dayDate = new Date(currentDate);
                            dayDate.setDate(dayDate.getDate() - (dayDate.getDay() || 7) + 1 + index);
                            const hasWodClassesForDay = dailyClasses.some(cls => cls.trainingType === "WOD");

                            return (
                                <Grid item xs={12} sm={6} md={1.7} lg={1.7} key={index}>
                                    <Box textAlign="center">
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Typography variant="h6" sx={{fontWeight: "bold", my: 1}}>
                                                {day} - {dayDate.toLocaleDateString("en-GB")}
                                            </Typography>

                                            {hasWodClassesForDay && (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        const newIndex = index;
                                                        setSelectedDayIndex(newIndex);
                                                        fetchDayLeaderboard(newIndex);
                                                    }}
                                                    size="small"
                                                    sx={{
                                                        ml: 0.5,
                                                        color: "goldenrod",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(218,165,32,0.1)"
                                                        }
                                                    }}
                                                >
                                                    <EmojiEventsIcon fontSize="small"/>
                                                </IconButton>
                                            )}
                                        </Box>
                                        <ClassSchedule
                                            classes={dailyClasses}
                                            attendeesCount={attendeesCount}
                                            onEdit={null} // No editing in marketing page
                                            onClassClick={handleClassClick} // Read-only click handler
                                            hideTrainer={true}
                                        />
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <ClassSchedule
                        classes={getClassesForSelectedDay(selectedDayIndex)}
                        attendeesCount={attendeesCount}
                        onEdit={null} // No editing in marketing page
                        onClassClick={handleClassClick} // Read-only click handler
                        weeklyView
                        hideTrainer={true}
                    />
                )}

                {/* Day Leaderboard Modal (from Classes.js) */}
                <Dialog
                    open={isDayLeaderboardOpen}
                    onClose={() => setDayLeaderboardOpen(false)}
                    maxWidth={false}
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: {xs: 0},
                            width: {xs: '100%'},
                            maxWidth: {xs: '100%'},
                            borderRadius: {xs: 0}
                        }
                    }}
                >
                    <DialogTitle>
                        <Box display="flex" alignItems="center">
                            <EmojiEventsIcon sx={{color: "goldenrod", mr: 1}}/>
                            <Typography variant="h6">
                                Daily WOD Leaderboard - {getFormattedDate()}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <ToggleButtonGroup
                            value={dayLeaderboardFilter}
                            exclusive
                            onChange={handleDayLeaderboardFilterChange}
                            aria-label="Filter results"
                            size="small"
                            sx={{mb: 2}}
                        >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="rx">Rx</ToggleButton>
                            <ToggleButton value="sc">Sc</ToggleButton>
                            <ToggleButton value="beg">Beg</ToggleButton>
                        </ToggleButtonGroup>

                        {loadingLeaderboard ? (
                            <Box display="flex" justifyContent="center" my={3}>
                                <CircularProgress/>
                            </Box>
                        ) : filteredDayLeaderboard.length > 0 ? (
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Class</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>
                                                Score
                                                <Typography
                                                    component="span"
                                                    color="text.secondary"
                                                    sx={{ml: 1, fontSize: '0.9em'}}
                                                >
                                                    (previous)
                                                </Typography>
                                            </TableCell>
                                            <TableCell>Type</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredDayLeaderboard.map((entry, index) => (
                                            <TableRow key={`${entry.id}-${index}`}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                                        {entry.classTime}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{entry.fullName}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        {entry.score}
                                                        {entry.previousScore && (
                                                            <Typography
                                                                component="span"
                                                                color="text.secondary"
                                                                sx={{ml: 1, fontSize: '0.9em'}}
                                                            >
                                                                ({entry.previousScore})
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{entry.scoreType?.toUpperCase()}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography align="center">
                                No results available for the selected filter.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDayLeaderboardOpen(false)} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={notificationOpen}
                    autoHideDuration={6000}
                    onClose={() => setNotificationOpen(false)}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    sx={{
                        zIndex: 9999,
                        top: '60px !important'
                    }}
                >
                    <Alert
                        onClose={() => setNotificationOpen(false)}
                        severity={notificationSeverity}
                        sx={{width: '100%'}}
                    >
                        {notificationMessage}
                    </Alert>
                </Snackbar>

                {/* Guest Registration Modal */}
                {selectedFreeClass && (
                    <GuestRegistrationModal
                        open={isGuestModalOpen}
                        onClose={() => {
                            setGuestModalOpen(false);
                            setSelectedFreeClass(null);
                        }}
                        classData={selectedFreeClass}
                        affiliateId={affiliate?.id}
                        onSuccess={handleGuestRegistrationSuccess}
                    />
                )}
            </Container>
        </Box>
    );
}